import { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import { useDashboardStore } from '../store/useDashboardStore';
import useDashboardApi from './useDashboardApi';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { getDateRangeFromPreset } from '../types/dashboard.types';
import type { DashboardSession, LoadingState } from '../types/dashboard.types';
import type {
  DashboardOverview,
  DashboardOverviewKpis,
  TimeSeriesPoint,
  DispositionSlice,
  SdrRanking,
  FunnelStage,
  OverviewGranularity,
  OverviewQueryParams,
} from '@types';


export interface RecentSession {
  id: string;
  shortId: string;
  sdrId: string;
  sdrName?: string;
  listNames: string;
  type: string;
  organizationName?: string;
  totalCalls: number;
  connectRate: number;
  appointmentsSet: number;
  status: string;
  startDate: string | null;
}

interface KpisApiResponse {
  kpis: DashboardOverviewKpis;
  callsOverTime: TimeSeriesPoint[];
  dispositionBreakdown: DispositionSlice[];
}

interface LeaderboardApiResponse {
  sdrLeaderboard: SdrRanking[];
  pipelineFunnel: FunnelStage[];
}

export interface UseDashboardOverviewVMReturn {
  overview: DashboardOverview | null;
  recentSessions: RecentSession[];
  loadingState: LoadingState;
  granularity: OverviewGranularity;
  prevPeriod: { from: Date; to: Date } | null;
  setGranularity: (g: OverviewGranularity) => void;
  refresh: () => Promise<void>;
}

/**
 * Mirrors backend OverviewService.calculatePreviousPeriod — returns the
 * immediately-prior window of the same length as the current selection.
 */
function calculatePreviousPeriod (from: Date, to: Date): { from: Date; to: Date } {
  const durationMs = to.getTime() - from.getTime();
  const prevTo = new Date(from.getTime() - 1);
  const prevFrom = new Date(prevTo.getTime() - durationMs);
  return { from: prevFrom, to: prevTo };
}

const RECENT_SESSIONS_LIMIT = 4;

export default function useDashboardOverviewVM (): UseDashboardOverviewVMReturn {
  const { currentUser } = useSessionStore();
  const dashboardApi = useDashboardApi();
  const {
    filters,
    overviewGranularity,
    setOverviewGranularity,
  } = useDashboardStore();

  const currentUserId = currentUser?.userId ?? '';

  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('LOADING');
  const [prevPeriod, setPrevPeriod] = useState<{ from: Date; to: Date } | null>(null);
  // Cache org/sdr name maps to avoid refetching every refresh
  const orgNameMapRef = useRef<Map<string, string>>(new Map());
  const sdrNameMapRef = useRef<Map<string, string>>(new Map());

  const buildEffectiveFilters = useCallback(() => {
    const effectiveFilters = { ...filters };
    if (effectiveFilters.dateRange?.from == null && effectiveFilters.dateRange?.preset !== 'custom') {
      const dateRange = getDateRangeFromPreset(effectiveFilters.dateRange?.preset ?? 'last7days');
      effectiveFilters.dateRange = {
        ...effectiveFilters.dateRange,
        from: dateRange.from,
        to: dateRange.to,
      };
    }
    return effectiveFilters;
  }, [filters]);

  const enrichSessions = useCallback(async (
    sessions: DashboardSession[],
  ): Promise<RecentSession[]> => {
    if (sessions.length === 0) {
      return [];
    }

    // Fetch missing org names
    const missingOrgIds = [
      ...new Set(
        sessions
          .map((s) => s.organizationId)
          .filter((id) => id !== '' && !orgNameMapRef.current.has(id)),
      ),
    ];

    if (missingOrgIds.length > 0) {
      const orgsResponse = await dashboardApi.getOrganizationsAsync();
      if (orgsResponse.success && orgsResponse.data !== undefined) {
        orgsResponse.data.forEach((org) => {
          orgNameMapRef.current.set(org.id, org.name);
        });
      }
    }

    // Fetch missing SDR names
    const missingSdrIds = [
      ...new Set(
        sessions
          .map((s) => s.sdrId)
          .filter((id) => id !== '' && !sdrNameMapRef.current.has(id)),
      ),
    ];

    if (missingSdrIds.length > 0) {
      const sdrsResponse = await dashboardApi.getSdrsAsync(
        currentUserId,
        missingSdrIds,
        null,
      );
      if (sdrsResponse.success && sdrsResponse.data.length > 0) {
        sdrsResponse.data.forEach((sdr) => {
          sdrNameMapRef.current.set(sdr.id, sdr.name);
        });
      }
    }

    return sessions.map((session) => ({
      id: session.id,
      shortId: session.shortId ?? session.id.slice(-4),
      sdrId: session.sdrId,
      sdrName: sdrNameMapRef.current.get(session.sdrId) ?? session.sdrName,
      listNames: session.listNames,
      type: session.type,
      organizationName: orgNameMapRef.current.get(session.organizationId) ?? session.organizationName,
      totalCalls: session.totalCalls ?? 0,
      connectRate: session.connectRate ?? 0,
      appointmentsSet: session.appointmentsSet ?? 0,
      status: session.status,
      startDate: typeof session.startAtSession === 'string'
        ? session.startAtSession
        : (session.startAtSession as { $date: string })?.$date ?? null,
    }));
  }, [currentUserId, dashboardApi]);

  const refresh = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }

    setLoadingState('LOADING');
    try {
      const effectiveFilters = buildEffectiveFilters();

      const dateFrom = effectiveFilters.dateRange.from instanceof Date
        ? effectiveFilters.dateRange.from
        : new Date(effectiveFilters.dateRange.from ?? new Date());
      const dateTo = effectiveFilters.dateRange.to instanceof Date
        ? effectiveFilters.dateRange.to
        : new Date(effectiveFilters.dateRange.to ?? new Date());

      setPrevPeriod(calculatePreviousPeriod(dateFrom, dateTo));

      const queryParams: OverviewQueryParams = {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        granularity: overviewGranularity,
      };

      if (effectiveFilters.orgIds.length > 0) {
        queryParams.orgId = effectiveFilters.orgIds[0];
      }

      if (effectiveFilters.sdrIds.length > 0) {
        queryParams.sdrId = effectiveFilters.sdrIds[0];
      }

      if (effectiveFilters.listTypes.length > 0) {
        queryParams.listTypes = effectiveFilters.listTypes;
      }

      if (effectiveFilters.searchQuery !== '') {
        queryParams.searchQuery = effectiveFilters.searchQuery;
      }

      // Fetch all 3 in parallel: KPIs, leaderboard, and recent sessions
      const [kpisResponse, leaderboardResponse, sessionsResponse] = await Promise.all([
        dashboardApi.getOverviewKpisAsync(queryParams),
        dashboardApi.getOverviewLeaderboardAsync(queryParams),
        dashboardApi.getSessionsAsync(
          currentUserId,
          effectiveFilters,
          1,
          RECENT_SESSIONS_LIMIT,
        ),
      ]);

      // ── Overview (KPIs + charts) ──
      let kpisData: KpisApiResponse | null = null;
      let leaderboardData: LeaderboardApiResponse | null = null;

      if (kpisResponse.success) {
        kpisData = kpisResponse.data as KpisApiResponse;
      }

      if (leaderboardResponse.success) {
        leaderboardData = leaderboardResponse.data as LeaderboardApiResponse;
      }

      if (kpisData !== null && leaderboardData !== null) {
        setOverview({
          kpis: kpisData.kpis,
          callsOverTime: kpisData.callsOverTime,
          dispositionBreakdown: kpisData.dispositionBreakdown,
          sdrLeaderboard: leaderboardData.sdrLeaderboard,
          pipelineFunnel: leaderboardData.pipelineFunnel,
        });

        void logInfo('Dashboard overview loaded from API.', 'DashboardOverviewVM.refresh', {
          totalCalls: kpisData.kpis?.totalCalls?.value,
          sdrCount: leaderboardData.sdrLeaderboard?.length,
        });
      } else {
        setOverview(null);

        void logError(
          'Failed to load dashboard overview.',
          'DashboardOverviewVM.refresh',
          {
            kpisSuccess: kpisResponse.success,
            kpisError: !kpisResponse.success ? kpisResponse.error : undefined,
            leaderboardSuccess: leaderboardResponse.success,
            leaderboardError: !leaderboardResponse.success ? leaderboardResponse.error : undefined,
          },
          true,
        );
      }

      // ── Recent Sessions (real endpoint) ──
      if (sessionsResponse.success && sessionsResponse.data?.data != null) {
        const enriched = await enrichSessions(sessionsResponse.data.data);
        setRecentSessions(enriched);
      } else {
        setRecentSessions([]);
      }

      setLoadingState('SUCCESS');
    } catch (error) {
      setOverview(null);
      setRecentSessions([]);
      setLoadingState('ERROR');

      void logError(
        'Failed to load dashboard overview.',
        'DashboardOverviewVM.refresh',
        { error },
        true,
      );
    }
  }, [currentUserId, filters, overviewGranularity, dashboardApi, buildEffectiveFilters, enrichSessions]);

  const setGranularity = useCallback((g: OverviewGranularity) => {
    setOverviewGranularity(g);
  }, [setOverviewGranularity]);

  useEffect(() => {
    if (currentUserId !== '') {
      void refresh();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, filters, overviewGranularity]);

  return {
    overview,
    recentSessions,
    loadingState,
    granularity: overviewGranularity,
    prevPeriod,
    setGranularity,
    refresh,
  };
}
